import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { zusToast } from '@zep/toast';

import { useMyFoldersWithItems } from '../api';
import { FOLDER_COPY } from '../consts/copy';
import { showSaveToFolderModal } from '../showSaveToFolderModal';
import type {
  SaveQuizTarget,
  SaveToFolderOptions,
} from '../types/saveQuizLocation.types';

/**
 * 방금 받아 온 목록을 "새 것"으로 인정하는 시간.
 *
 * 정확성은 이 값이 아니라 무효화가 지킨다 — 폴더를 만들거나 옮기면 `folderKeys.all` 이
 * 무효화돼 staleTime 과 무관하게 다시 받는다. 이 값은 **연속으로 여닫을 때 매번 기다리지
 * 않게** 하는 용도다.
 */
const FOLDER_LIST_FRESH_MS = 60_000;

/**
 * 저장 위치 선택 모달을 **목록을 받아 둔 뒤** 연다.
 *
 * 예전에는 `overlay.open` 이 즉시 반환하고 조회는 모달 안에서 시작했다. 그래서 `내 퀴즈`
 * 한 줄만 있는 모달이 떴다가 폴더가 도착하면서 높이가 점프했고, "폴더가 없는 사용자"와
 * "아직 안 받은 상태"가 화면에서 구분되지 않았다. 모달 높이가 폴더 수에 따라 달라지므로
 * 스켈레톤으로 자리를 잡아 두기도 어렵다 — 그래서 **채워진 상태로 여는 쪽**을 택했다.
 *
 * 같은 이유로 실패하면 모달을 아예 열지 않는다. 빈 모달을 띄워 놓고 그 안에서 실패를
 * 알리면 사용자는 고를 것이 없는 화면을 닫아야 한다.
 * 같은 패턴: `pages/board/[boardId]/features/approval/hooks/useApproveAllFlow.ts`.
 *
 * 훅인 이유는 `queryClient` 때문이다. 이 앱에는 전역 QueryClient 싱글턴이 없고
 * (`_app.page.tsx` 가 `useState` 로 만든다) 진입점이 전부 컴포넌트·훅이라 문제가 없다.
 */
export const useShowSaveToFolderModal = () => {
  const queryClient = useQueryClient();
  /** 느린 네트워크에서 연타하면 요청과 모달이 겹친다. 한 번에 하나만 연다. */
  const isOpeningRef = useRef(false);

  return useCallback(
    async (quiz: SaveQuizTarget, options?: SaveToFolderOptions) => {
      if (isOpeningRef.current) return;
      isOpeningRef.current = true;

      try {
        await queryClient.fetchInfiniteQuery({
          ...useMyFoldersWithItems.getFetchOptions(),
          staleTime: FOLDER_LIST_FRESH_MS,
          /*
            전역 기본 재시도는 최대 4회에 간격이 30초까지 늘어난다(`_app.page.tsx`).
            여기서는 그동안 버튼이 죽은 것처럼 보이므로 첫 실패에 바로 알린다.
          */
          retry: false,
        });
      } catch {
        zusToast.error(FOLDER_COPY.saveModalLoadError);
        return;
      } finally {
        isOpeningRef.current = false;
      }

      await showSaveToFolderModal(quiz, options);
    },
    [queryClient],
  );
};
